import React, { useState, useRef, useLayoutEffect } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, position = 'auto' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current && position === 'auto') {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate space available in each direction
      const spaceTop = containerRect.top;
      const spaceBottom = viewportHeight - containerRect.bottom;
      const spaceLeft = containerRect.left;
      const spaceRight = viewportWidth - containerRect.right;

      // Determine best position based on available space
      let bestPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

      if (spaceTop >= tooltipRect.height + 10) {
        bestPosition = 'top';
      } else if (spaceBottom >= tooltipRect.height + 10) {
        bestPosition = 'bottom';
      } else if (spaceLeft >= tooltipRect.width + 10) {
        bestPosition = 'left';
      } else if (spaceRight >= tooltipRect.width + 10) {
        bestPosition = 'right';
      } else {
        // If no space is adequate, choose the side with most space
        const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
        if (maxSpace === spaceTop) bestPosition = 'top';
        else if (maxSpace === spaceBottom) bestPosition = 'bottom';
        else if (maxSpace === spaceLeft) bestPosition = 'left';
        else bestPosition = 'right';
      }

      setActualPosition(bestPosition);
    } else if (position !== 'auto') {
      setActualPosition(position);
    }
  }, [isVisible, position]);

  const getPositionClasses = (pos: 'top' | 'bottom' | 'left' | 'right') => {
    switch (pos) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = (pos: 'top' | 'bottom' | 'left' | 'right') => {
    switch (pos) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-900';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-blue-900';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-blue-900';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-blue-900';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-900';
    }
  };

  return (
    <div 
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 ${getPositionClasses(actualPosition)}`}
        >
          <div className="bg-blue-900 text-white text-xs rounded-md py-2 px-3 w-40 whitespace-pre-line shadow-lg font-medium leading-relaxed">
            {content}
          </div>
          <div className={`absolute w-0 h-0 ${getArrowClasses(actualPosition)}`}></div>
        </div>
      )}
    </div>
  );
};

export default Tooltip; 